import { Text } from '@mantine/core'
import type { TextProps } from '@mantine/core'
import React from 'react'
import type { CSSProperties, ElementType, ReactNode } from 'react'

/**
 * Figma typography variants (Raund-v2)
 * font-family: 'Noto Sans JP'
 */
export type TypographyVariant =
  | 'h1' // H1 DisplayXLarge  : 28px / 40 / Bold
  | 'h2' // H2 DisplayLarge   : 24px / 36 / Bold
  | 'h3' // H3 DisplayMedium  : 20px / 32 / Bold
  | 'h4' // H4 DisplaySmall   : 18px / 28 / Bold
  | 'h5' // H5 DisplayXSmall  : 16px / 24 / Bold
  | 'subtitle1' // Subtitle1         : 16px / 24 / Bold
  | 'subtitle2' // Subtitle2         : 13px / 20 / Bold
  | 'body1' // Body1             : 15px / 24 / Regular, ls:-0.01em
  | 'body2' // Body2             : 13px / 20 / Regular, ls:-0.01em
  | 'body3' // Body3             : 14px / 22 / Regular, ls:-0.01em
  | 'body4' // Body4             : 16px / 24 / Regular, ls:-0.01em
  | 'button1' // Button1           : 15px / 24 / Bold
  | 'button2' // Button2           : 13px / 16 / Bold
  | 'caption1' // Caption1          : 12px / 16 / Regular
  | 'caption2' // Caption2          : 10px / 16 / Regular
  | 'overline' // Overline          : 11px / 16 / Bold

type VariantStyle = {
  fontSize: string
  fontWeight: number
  lineHeight: string
  letterSpacing?: string
}

export const VARIANT_STYLES: Record<TypographyVariant, VariantStyle> = {
  h1: { fontSize: '28px', fontWeight: 700, lineHeight: '40px' },
  h2: { fontSize: '24px', fontWeight: 700, lineHeight: '36px' },
  h3: { fontSize: '20px', fontWeight: 700, lineHeight: '32px' },
  h4: { fontSize: '18px', fontWeight: 700, lineHeight: '28px' },
  h5: { fontSize: '16px', fontWeight: 700, lineHeight: '24px' },
  subtitle1: { fontSize: '16px', fontWeight: 700, lineHeight: '24px' },
  subtitle2: { fontSize: '13px', fontWeight: 700, lineHeight: '20px' },
  body1: { fontSize: '15px', fontWeight: 400, lineHeight: '24px', letterSpacing: '-0.01em' },
  body2: { fontSize: '13px', fontWeight: 400, lineHeight: '20px', letterSpacing: '-0.01em' },
  body3: { fontSize: '14px', fontWeight: 400, lineHeight: '22px', letterSpacing: '-0.01em' },
  body4: { fontSize: '16px', fontWeight: 400, lineHeight: '24px', letterSpacing: '-0.01em' },
  button1: { fontSize: '15px', fontWeight: 700, lineHeight: '24px' },
  button2: { fontSize: '13px', fontWeight: 700, lineHeight: '16px' },
  caption1: { fontSize: '12px', fontWeight: 400, lineHeight: '16px' },
  caption2: { fontSize: '10px', fontWeight: 400, lineHeight: '16px' },
  overline: { fontSize: '11px', fontWeight: 700, lineHeight: '16px' },
}

const DEFAULT_ELEMENTS: Record<TypographyVariant, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  subtitle1: 'p',
  subtitle2: 'p',
  body1: 'p',
  body2: 'p',
  body3: 'p',
  body4: 'p',
  button1: 'span',
  button2: 'span',
  caption1: 'span',
  caption2: 'span',
  overline: 'span',
}

export type TypographyProps = Omit<TextProps, 'size' | 'fw' | 'lh' | 'ff' | 'component'> & {
  /** Figma の typography variant 名 */
  variant: TypographyVariant
  /** デフォルトの HTML 要素を上書きする場合に指定 */
  component?: ElementType
  children?: ReactNode
}

/**
 * Raund-v2 Figma の typography スタイルを適用するコンポーネント。
 * Mantine の Text をベースにしており、className / style / c (color) / m / p などの
 * Mantine Text Props を透過的に受け取れる。
 *
 * @example
 * <Typography variant="h1">見出しテキスト</Typography>
 * <Typography variant="body1" c="#8A8B8F">サブテキスト</Typography>
 * <Typography variant="overline" component="label">ラベル</Typography>
 */
export function Typography({ variant, component, style, children, ...props }: TypographyProps) {
  const vs = VARIANT_STYLES[variant]
  const element = component ?? DEFAULT_ELEMENTS[variant]

  const variantStyle: CSSProperties = {
    fontFamily: "'Noto Sans JP', sans-serif",
    fontSize: vs.fontSize,
    fontWeight: vs.fontWeight,
    lineHeight: vs.lineHeight,
    ...(vs.letterSpacing !== undefined ? { letterSpacing: vs.letterSpacing } : {}),
  }

  return (
    <Text
      renderRoot={(rootProps: Record<string, unknown>) =>
        React.createElement(
          element as string,
          {
            ...rootProps,
            style: {
              ...variantStyle,
              ...(rootProps.style as CSSProperties | undefined),
              ...(style as CSSProperties | undefined),
            },
          },
          children,
        )
      }
      {...props}
    />
  )
}
